import {
  getStorage,
  ref,
  uploadBytes as firebaseUploadBytes,
  uploadBytesResumable as firebaseUploadBytesResumable,
  getDownloadURL as firebaseGetDownloadURL,
  deleteObject as firebaseDeleteObject,
  type FirebaseStorage,
  type StorageReference,
  type UploadTask,
  type UploadResult,
  type UploadTaskSnapshot,
} from 'firebase/storage'
import { app } from './app'
import { toUserFacingError } from '@/lib/errors'

const storage: FirebaseStorage = getStorage(app)

async function uploadBytes(
  ...args: Parameters<typeof firebaseUploadBytes>
): Promise<UploadResult> {
  try {
    return await firebaseUploadBytes(...args)
  } catch (error) {
    throw toUserFacingError(
      error,
      'We could not upload this file. Please try again.',
      'storage/upload',
    )
  }
}

function normalizeUploadTask(task: UploadTask): UploadTask {
  const originalOn = task.on.bind(task)
  task.on = ((...args: Parameters<UploadTask['on']>) => {
    const nextOrObserver = args[1]
    const errorCallback = args[2]

    if (nextOrObserver && typeof nextOrObserver === 'object') {
      const observer = nextOrObserver as { error?: (error: unknown) => unknown }
      args[1] = {
        ...observer,
        error: (error: unknown) =>
          observer.error?.(
            toUserFacingError(
              error,
              'We could not upload this file. Please try again.',
              'storage/upload',
            ) as never,
          ),
      } as typeof args[1]
    } else if (typeof errorCallback === 'function') {
      args[2] = ((error: unknown) =>
        errorCallback(
          toUserFacingError(
            error,
            'We could not upload this file. Please try again.',
            'storage/upload',
          ) as never,
        )) as typeof args[2]
    }

    return originalOn(...args)
  }) as UploadTask['on']
  return task
}

function uploadBytesResumable(
  ...args: Parameters<typeof firebaseUploadBytesResumable>
): UploadTask {
  return normalizeUploadTask(firebaseUploadBytesResumable(...args))
}

async function getDownloadURL(ref: StorageReference): Promise<string> {
  try {
    return await firebaseGetDownloadURL(ref)
  } catch (error) {
    throw toUserFacingError(
      error,
      'We could not prepare this file. Please try again.',
      'storage/download-url',
    )
  }
}

async function deleteObject(ref: StorageReference): Promise<void> {
  try {
    await firebaseDeleteObject(ref)
  } catch (error) {
    throw toUserFacingError(
      error,
      'We could not remove this file. Please try again.',
      'storage/delete',
    )
  }
}

export { storage, ref, uploadBytes, uploadBytesResumable, getDownloadURL, deleteObject }
export type { StorageReference, UploadTaskSnapshot }
